import React, { useMemo } from 'react'
import { DesignTokens } from '../types'
import {
  COLOR_VALUES,
  COLOR_VALUES_DARK,
  COLOR_VALUES_LIGHT,
  COLOR_VALUES_BG,
  NEUTRAL_BG,
  NEUTRAL_TEXT,
  NEUTRAL_TEXT_SECONDARY,
  NEUTRAL_BORDER,
} from '../data/tailwindColors'
import { RADIUS_VALUES, SHADOW_VALUES } from '../data/styleTokens'
import {
  FONT_SIZE_VALUES,
  FONT_WEIGHT_VALUES,
  getFontFamilyValue,
  getFontOption,
} from '../data/typography'

interface PreviewProps {
  tokens: DesignTokens
}

export function Preview({ tokens }: PreviewProps) {
  const styles = useMemo(() => {
    const primary = tokens.primaryColor
    const neutral = tokens.neutralColor

    return {
      primary: COLOR_VALUES[primary],
      primaryLight: COLOR_VALUES_LIGHT[primary],
      primaryDark: COLOR_VALUES_DARK[primary],
      primaryBg: COLOR_VALUES_BG[primary],
      neutralBg: NEUTRAL_BG[neutral],
      neutralText: NEUTRAL_TEXT[neutral],
      neutralTextSecondary: NEUTRAL_TEXT_SECONDARY[neutral],
      neutralBorder: NEUTRAL_BORDER[neutral],
      radius: RADIUS_VALUES[tokens.borderRadius],
      shadow: SHADOW_VALUES[tokens.shadow],
      displayFamily: getFontFamilyValue(tokens.displayFont),
      bodyFamily: getFontFamilyValue(tokens.bodyFont),
      monoFamily: getFontFamilyValue(tokens.monoFont),
      displayWeight: FONT_WEIGHT_VALUES[tokens.displayWeight],
      bodyWeight: FONT_WEIGHT_VALUES[tokens.bodyWeight],
      monoWeight: FONT_WEIGHT_VALUES[tokens.monoWeight],
      displaySize: FONT_SIZE_VALUES[tokens.displaySize],
      bodySize: FONT_SIZE_VALUES[tokens.bodySize],
      monoSize: FONT_SIZE_VALUES[tokens.monoSize],
    }
  }, [tokens])

  const buttonRadius =
    tokens.borderRadius === 'full' ? '9999px' : RADIUS_VALUES[tokens.borderRadius]

  return (
    <div
      className="p-6 rounded-xl border"
      style={{
        backgroundColor: styles.neutralBg,
        borderColor: styles.neutralBorder,
        fontFamily: styles.bodyFamily,
      }}
    >
      <h2
        className="text-sm uppercase tracking-wide mb-4"
        style={{ color: styles.neutralTextSecondary }}
      >
        Live Preview
      </h2>

      {/* Card */}
      <div
        className="p-5 mb-6"
        style={{
          backgroundColor: 'white',
          borderRadius: styles.radius,
          boxShadow: styles.shadow,
          border: `1px solid ${styles.neutralBorder}`,
        }}
      >
        <h3
          className="mb-2"
          style={{
            color: styles.neutralText,
            fontFamily: styles.displayFamily,
            fontWeight: styles.displayWeight,
            fontSize: styles.displaySize,
          }}
        >
          Welcome to Your App
        </h3>
        <p
          className="mb-4"
          style={{
            color: styles.neutralTextSecondary,
            fontSize: styles.bodySize,
            fontWeight: styles.bodyWeight,
          }}
        >
          This is how your design system will look. Customize the tokens on the left to see changes
          in real-time.
        </p>

        {/* Buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            className="px-4 py-2 text-sm font-medium text-white transition-colors cursor-pointer"
            style={{
              backgroundColor: styles.primary,
              borderRadius: buttonRadius,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = styles.primaryDark)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = styles.primary)}
          >
            Primary Button
          </button>
          <button
            className="px-4 py-2 text-sm font-medium border transition-colors cursor-pointer"
            style={{
              color: styles.primary,
              borderColor: styles.primary,
              backgroundColor: 'transparent',
              borderRadius: buttonRadius,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = styles.primaryBg)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            Secondary Button
          </button>
          <button
            className="px-4 py-2 text-sm font-medium transition-colors cursor-pointer"
            style={{
              color: styles.neutralTextSecondary,
              backgroundColor: 'transparent',
              borderRadius: buttonRadius,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = styles.neutralText)}
            onMouseLeave={(e) => (e.currentTarget.style.color = styles.neutralTextSecondary)}
          >
            Ghost Button
          </button>
        </div>

        {/* Input */}
        <div className="mb-4">
          <label
            className="block text-xs font-medium mb-1"
            style={{ color: styles.neutralTextSecondary }}
          >
            Email Address
          </label>
          <input
            type="email"
            placeholder="you@example.com"
            className="w-full px-3 py-2 text-sm outline-none transition-shadow"
            style={{
              color: styles.neutralText,
              backgroundColor: 'white',
              border: `1px solid ${styles.neutralBorder}`,
              borderRadius: styles.radius,
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = styles.primary
              e.currentTarget.style.boxShadow = `0 0 0 3px ${styles.primaryBg}`
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = styles.neutralBorder
              e.currentTarget.style.boxShadow = 'none'
            }}
          />
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-2">
          <span
            className="px-2.5 py-0.5 text-xs font-medium"
            style={{
              color: styles.primaryDark,
              backgroundColor: styles.primaryBg,
              borderRadius: buttonRadius,
            }}
          >
            New Feature
          </span>
          <span
            className="px-2.5 py-0.5 text-xs font-medium"
            style={{
              color: styles.neutralTextSecondary,
              backgroundColor: styles.neutralBorder,
              borderRadius: buttonRadius,
            }}
          >
            Neutral Badge
          </span>
        </div>
      </div>

      {/* Typography Sample */}
      <div
        className="p-4"
        style={{
          backgroundColor: 'white',
          borderRadius: styles.radius,
          border: `1px solid ${styles.neutralBorder}`,
        }}
      >
        <h4
          className="mb-1"
          style={{
            color: styles.neutralText,
            fontFamily: styles.displayFamily,
            fontWeight: styles.displayWeight,
            fontSize: styles.displaySize,
          }}
        >
          Typography
        </h4>
        <p className="text-sm" style={{ color: styles.neutralTextSecondary }}>
          Display: {getFontOption(tokens.displayFont).name} · Body: {getFontOption(tokens.bodyFont).name} · Mono:{' '}
          {getFontOption(tokens.monoFont).name}
        </p>
        <p
          className="mt-2"
          style={{
            color: styles.neutralText,
            fontFamily: styles.bodyFamily,
            fontWeight: styles.bodyWeight,
            fontSize: styles.bodySize,
          }}
        >
          The quick brown fox jumps over the lazy dog.
        </p>
        <pre
          className="mt-3 px-3 py-2 rounded-md overflow-x-auto"
          style={{
            backgroundColor: styles.neutralBorder,
            fontFamily: styles.monoFamily,
            fontWeight: styles.monoWeight,
            fontSize: styles.monoSize,
            color: styles.neutralText,
          }}
        >
          {`const designSystem = '${getFontOption(tokens.displayFont).name}'`}
        </pre>
      </div>
    </div>
  )
}
